/**
 * NotificationBell — compact icon button that shows unread count badge
 * and navigates to /notifications on click (no dropdown).
 * Still polls every 60 s to keep the badge count fresh.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationsAPI, { type AppNotification } from '@/api/notifications.api';
import { useAuthStore } from '@/stores/auth.store';
import { ROUTES } from '@/router/routes';

const POLL_MS     = 60_000;
const MAX_POLL_MS = 30 * 60_000;

function playNotificationChime() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(587.33, ctx.currentTime);
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.3);
  } catch {}
}

export default function NotificationBell() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const userId = (user as any)?.objectId || (user as any)?.id || '';

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const failsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevUnreadRef = useRef<number | null>(null);

  const unread = notifications.filter(n => !n.read).length;

  const scheduleNext = useCallback((delayMs: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => poll(), delayMs); // eslint-disable-line
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const poll = useCallback(async () => {
    if (!userId) return;
    try {
      const list = await NotificationsAPI.getForUser(userId);
      setNotifications(list);
      const newUnread = list.filter(n => !n.read).length;
      if (prevUnreadRef.current !== null && newUnread > prevUnreadRef.current) {
        playNotificationChime();
      }
      prevUnreadRef.current = newUnread;
      failsRef.current = 0;
      scheduleNext(POLL_MS);
    } catch {
      failsRef.current += 1;
      const delay = Math.min(POLL_MS * 2 ** failsRef.current, MAX_POLL_MS);
      scheduleNext(delay);
    }
  }, [userId, scheduleNext]);

  useEffect(() => {
    poll();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [poll]);

  if (!userId) return null;

  return (
    <button
      id="notification-bell-btn"
      onClick={() => navigate(ROUTES.NOTIFICATIONS)}
      aria-label={`Уведомления${unread > 0 ? ` (${unread} непрочитанных)` : ''}`}
      style={{
        position: 'relative',
        width: 30, height: 30,
        borderRadius: 8,
        border: '1.5px solid transparent',
        background: 'rgba(0,0,0,.04)',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--color-text-muted, #6b7280)',
        transition: 'all .15s',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(99,102,241,.08)';
        (e.currentTarget as HTMLButtonElement).style.color = '#6366f1';
        (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(99,102,241,.25)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,0,0,.04)';
        (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-muted, #6b7280)';
        (e.currentTarget as HTMLButtonElement).style.borderColor = 'transparent';
      }}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>

      {unread > 0 && (
        <span style={{
          position: 'absolute', top: -3, right: -3,
          minWidth: 16, height: 16, borderRadius: 8,
          background: '#ef4444', color: '#fff',
          fontSize: 9, fontWeight: 800,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 3px', lineHeight: 1,
          border: '1.5px solid #fff',
        }}>
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
}
